/**
 * features/exams/MyDutiesPane.tsx — "when am I in an exam room?"
 * ------------------------------------------------------------------
 * The other half of the rota, and the reason it is a separate screen rather
 * than a filter on the invigilation one (the school, 2026-09-18): a teacher
 * wants their own five duties, not the nine hundred rows the office works on.
 *
 * A DAY IS A ROW, MORNING BESIDE AFTERNOON (the school, 2026-09-18). Whether
 * a duty is before or after lunch is the first thing a teacher needs and the
 * easiest to misread off a list — in two columns it is the position on the
 * page, read before the words. An empty column is left empty and says so; on
 * a phone the columns stack, so each still carries its own heading.
 *
 * A DUTY IS A SESSION, NOT A PAPER. Papers in a session run concurrently in
 * one room, so a teacher named on two of them has ONE duty covering both, its
 * hours the union of the windows (`sessionDutiesFor`). It says the two things
 * a printed rota cannot: which lesson each duty takes them out of, by name,
 * and when they are due in earlier than the paper starts — the key-time
 * window, which is the one people arrive late for.
 *
 * A teacher sees themselves. Whoever runs the exams may look up anyone, which
 * is how "what have you given me?" gets answered without a spreadsheet.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, ClipboardCheck, Clock, Download, Loader2, Moon, ShieldCheck, Sun, SunMedium, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { PanelHeader, SoftCard, StatChip, StatStrip, glassFieldClass } from '@/components/ui/design';
import {
  clashesFor, dayOf, dutyDays, sessionDutiesFor,
  type DutyClash, type DutySession, type PeriodTimes,
} from '@/lib/examDuties';
import type { TimetableSlot } from '@/lib/timetable';
import { registerId } from '@/lib/examRegister';
import { gaboroneInstant, sessionName } from '@/lib/examTimetable';
import { useExamDuties, periodTimesFor } from './useExamDuties';
import { ROLE_CARD, ROLE_CHIP, ROLE_ICON, ROLE_LABEL, leadRole } from './dutyStyle';
import { shortDay } from './ExamsHub';

/** A session with the lessons it would cost, worked out once. */
interface MySession extends DutySession {
  clashes: DutyClash[];
  ends: number;
}

export default function MyDutiesPane({ seriesId, now }: { seriesId: string; now: number }) {
  const { userData, permissions } = useAuth();
  const { teachers } = useData();
  const { papers, slots, schedule, loading, error, internal } = useExamDuties(seriesId);

  const mine = userData?.linkedTeacherId ?? '';
  const [who, setWho] = useState('');
  const [printing, setPrinting] = useState(false);
  /* Duties already done are off the live list (the school, 2026-10-05); the
     office checking what someone was given can still show them. */
  const [showPast, setShowPast] = useState(false);
  useEffect(() => { setWho(mine); }, [mine]);

  const sessions = useMemo<MySession[]>(() => sessionDutiesFor(who, papers).map((s) => ({
    ...s,
    /* Per SPAN, not per paper: two concurrent papers in one room would
       otherwise warn about the same lesson twice. */
    clashes: clashesOf(who, s, slots, periodTimesFor(schedule, dayOf(s.date))),
    ends: gaboroneInstant(s.date, s.spans[s.spans.length - 1]?.to || '23:59'),
  })), [who, papers, slots, schedule]);

  const days = useMemo(() => dutyDays(sessions) as unknown as {
    date: string; am: MySession | null; mm: MySession | null; pm: MySession | null; other: MySession[];
  }[], [sessions]);

  if (loading) return <p className="py-10 text-sm text-[var(--sp-muted)]">Loading your duties…</p>;
  if (error) return <SoftCard><p className="text-sm text-rose-600 dark:text-rose-400">{error}</p></SoftCard>;

  const picker = permissions.canManageExamTimetables ? (
    <label className="flex flex-wrap items-center gap-2 text-sm text-[var(--sp-muted)]">
      Whose duties
      <select value={who} onChange={(e) => setWho(e.target.value)} className={glassFieldClass('w-auto min-w-[14rem]')}>
        {mine && <option value={mine}>Mine</option>}
        {(teachers ?? [])
          .filter((t) => t.id !== mine)
          .slice()
          .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
          .map((t) => <option key={t.id} value={t.id}>{t.fullName || t.displayName}</option>)}
      </select>
    </label>
  ) : null;

  if (!who) {
    return (
      <SoftCard>
        <p className="text-sm text-[var(--sp-body)]">
          This account is not linked to a staff record, so it has no duties of its own.
          {permissions.canManageExamTimetables ? ' Pick a teacher to see theirs.' : ' Ask the office to link it.'}
        </p>
        {picker && <div className="mt-3">{picker}</div>}
      </SoftCard>
    );
  }

  /* The teachers asked for the same thing the candidates did (2026-09-22): a
     rota behind a login is no use in a corridor at half past seven. jsPDF and
     the embedded font are about a megabyte, so the module is imported ON THE
     CLICK and never reaches a first load. */
  const whoName = teachers?.find((t) => t.id === who);
  const printName = (whoName?.fullName || whoName?.displayName || 'My duties').trim();
  const printSheet = async () => {
    setPrinting(true);
    try {
      const { buildExamDutiesPdf } = await import('@/lib/examDutiesPdf');
      const pdf = await buildExamDutiesPdf({
        teacherName: printName,
        seriesTitle: seriesId,
        sessions: sessions.map((s) => ({
          date: s.date,
          session: s.session,
          spans: s.spans,
          keyTime: s.roles.includes('keyTime'),
          marker: s.entries.find((e) => e.duty.marker)?.duty.marker,
          papers: s.entries.map(({ paper, duty }) => ({
            code: paper.code,
            subject: paper.subject,
            from: duty.role === 'keyTime' ? duty.from : paper.start,
            to: duty.role === 'keyTime' ? duty.to : paper.end,
            keyTime: duty.role === 'keyTime',
            entries: paper.entries,
          })),
          /* Formatted here, exactly as the cards word it, so the page and the
             screen cannot drift into describing the same clash differently. */
          clashes: s.clashes.map((c) => (
            `${(c.slot.classes ?? []).join(', ')} ${c.subject}`.trim()
            + ` (period${c.periods.length === 1 ? '' : 's'} ${c.periods.join(', ')})`
          )),
        })),
      });
      pdf.save(`invigilation-${printName.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } finally {
      setPrinting(false);
    }
  };

  const next = sessions.find((s) => s.ends > now) ?? null;
  const minutes = sessions.reduce((n, s) => n + s.minutes, 0);
  const lessons = sessions.reduce((n, s) => n + s.clashes.length, 0);
  const done = sessions.filter((s) => s.ends <= now).length;
  /* A day stays while any of its duties is still to come. */
  const liveDays = showPast ? days : days
    .map((d) => ({
      ...d,
      am: d.am && d.am.ends > now ? d.am : null,
      mm: d.mm && d.mm.ends > now ? d.mm : null,
      pm: d.pm && d.pm.ends > now ? d.pm : null,
      other: d.other.filter((s) => s.ends > now),
    }))
    .filter((d) => d.am || d.mm || d.pm || d.other.length);
  /* LKC's own exams run a mid-morning session too: three columns when any
     day of this series has one, else Cambridge's two. */
  const threeSessions = days.some((d) => d.mm);

  return (
    <div className="space-y-5">
      <StatStrip>
        <StatChip
          tone="navy" icon={CalendarDays}
          value={next ? shortDay(next.date) : '—'}
          label={next
            ? `next — ${internal ? sessionName(next.session, true) : sessionName(next.session, false).toLowerCase()}, in at ${next.spans[0]?.from ?? '—'}`
            : 'no duties left'}
        />
        <StatChip tone="sky" icon={Users} value={sessions.length} label={`duties this series · ${done} done`} />
        <StatChip tone="slate" icon={Clock} value={(minutes / 60).toFixed(1)} label="hours in an exam room" />
        <StatChip
          tone={lessons ? 'amber' : 'emerald'} icon={lessons ? AlertTriangle : Clock}
          value={lessons} label={lessons ? 'lessons a duty falls on' : 'no lessons affected'}
        />
      </StatStrip>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {picker ?? <span />}
        <button
          type="button" onClick={() => { void printSheet(); }}
          disabled={printing || !sessions.length}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryLight disabled:opacity-50 dark:text-white"
        >
          {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Print my timetable
        </button>
      </div>

      {!sessions.length ? (
        <SoftCard pad="">
          <p className="px-5 py-10 text-center text-sm text-[var(--sp-muted)]">
            Nothing on the invigilation rota for this series.
          </p>
        </SoftCard>
      ) : (
        <SoftCard pad="">
          <PanelHeader
            icon={ShieldCheck} title="Every duty, day by day"
            subtitle={internal
              ? 'Session 1 on the left, Session 2 on the right.'
              : 'Morning on the left, afternoon on the right. A violet card is an FCS window — you are due in before the paper starts'}
          />
          {done > 0 && (
            <p className="border-b border-[var(--sp-line)] px-4 py-2 text-xs text-[var(--sp-muted)]">
              {showPast ? 'Showing every duty, including' : 'Not showing'} the {done} already done.{' '}
              <button type="button" onClick={() => setShowPast((v) => !v)} className="font-semibold text-primary hover:underline dark:text-primarySoft">
                {showPast ? 'Hide them' : 'Show them'}
              </button>
            </p>
          )}
          {!liveDays.length && (
            <p className="px-5 py-8 text-center text-sm text-[var(--sp-muted)]">No duties left in this series.</p>
          )}
          <ul className="divide-y divide-[var(--sp-line)]">
            {liveDays.map((day) => (
              <li key={day.date} className="px-4 py-3">
                <p className="mb-2 text-sm font-bold text-ink dark:text-slate-100">{shortDay(day.date)}</p>
                <div className={`grid gap-3 ${threeSessions ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                  <SessionCell label={sessionName('AM', internal)} icon={Sun} session={day.am} now={now} seriesId={seriesId} />
                  {threeSessions && <SessionCell label={sessionName('MM', internal)} icon={SunMedium} session={day.mm} now={now} seriesId={seriesId} />}
                  <SessionCell label={sessionName('PM', internal)} icon={Moon} session={day.pm} now={now} seriesId={seriesId} />
                </div>
                {day.other.map((s) => (
                  <div key={s.key} className="mt-3">
                    <SessionCell label="Time to be confirmed" icon={Clock} session={s} now={now} seriesId={seriesId} />
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </SoftCard>
      )}
    </div>
  );
}

/** The lessons a whole session costs — asked per merged span and deduplicated,
 *  so two papers in one room do not report one lesson twice. */
function clashesOf(
  teacherId: string,
  session: DutySession,
  slots: TimetableSlot[],
  periods: PeriodTimes[],
): DutyClash[] {
  const seen = new Map<string, DutyClash>();
  for (const span of session.spans) {
    for (const clash of clashesFor(teacherId, { date: session.date, ...span }, slots, periods)) {
      if (!seen.has(clash.slot.id)) seen.set(clash.slot.id, clash);
    }
  }
  return [...seen.values()];
}

function SessionCell({ label, icon: Icon, session, now, seriesId }: {
  label: string;
  icon: typeof Sun;
  session: MySession | null;
  now: number;
  seriesId: string;
}) {
  if (!session) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--sp-line)] px-3 py-2.5">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--sp-muted)]">
          <Icon className="h-3.5 w-3.5" /> {label}
        </p>
        <p className="mt-1 text-sm text-[var(--sp-muted)]">No duty</p>
      </div>
    );
  }

  const past = session.ends <= now;
  const lead = leadRole(session.roles);
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${ROLE_CARD[lead]} ${past ? 'opacity-55' : ''}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${
          lead === 'keyTime' ? ROLE_ICON.keyTime : 'text-[var(--sp-muted)]'}`}>
          <Icon className="h-3.5 w-3.5" /> {label}
        </p>
        <p className="text-sm font-bold tabular-nums text-ink dark:text-slate-100">
          {session.spans.map((s) => `${s.from}–${s.to}`).join(', ')}
        </p>
      </div>

      <p className="mt-1 flex flex-wrap gap-1.5">
        {session.roles.map((role) => (
          <span key={role} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${ROLE_CHIP[role]}`}>
            {ROLE_LABEL[role]}
          </span>
        ))}
        {session.entries.some((e) => e.duty.marker) && (
          <span className="text-[11px] text-[var(--sp-muted)]">({session.entries.find((e) => e.duty.marker)?.duty.marker})</span>
        )}
      </p>

      <ul className="mt-1.5 space-y-0.5">
        {session.entries.map(({ paper, duty }, i) => (
          <li key={`${paper.id}-${i}`} className="text-sm text-[var(--sp-body)]">
            <span className="font-semibold text-ink dark:text-slate-100">{paper.code}</span> {paper.subject}
            <span className="text-[var(--sp-muted)]">
              {' · '}{duty.role === 'keyTime' ? `FCS ${duty.from}–${duty.to}` : `${paper.start}–${paper.end}`}
              {' · '}{paper.entries} candidate{paper.entries === 1 ? '' : 's'}
            </span>
            {duty.role === 'invigilator' && (
              <a
                className="ml-1.5 whitespace-nowrap font-semibold text-primary hover:underline"
                href={`#/examRegisters/${registerId(seriesId, paper.date, paper.session, paper.code)}`}
              >
                <ClipboardCheck className="inline h-3.5 w-3.5" /> register
              </a>
            )}
          </li>
        ))}
      </ul>
      {session.entries.length > 1 && (
        <p className="mt-1 text-[11px] text-[var(--sp-muted)]">
          Both papers are sat in the same room at the same time — one duty.
        </p>
      )}

      {!!session.clashes.length && (
        <p className="mt-1.5 inline-flex flex-wrap items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          You are timetabled to teach {session.clashes.map((c) => (
            `${(c.slot.classes ?? []).join(', ')} ${c.subject}`.trim()
            + ` (period${c.periods.length === 1 ? '' : 's'} ${c.periods.join(', ')})`
          )).join('; ')}
        </p>
      )}
    </div>
  );
}
